import { withTranslation } from 'react-i18next';

import Loading from '../../../../components/Loading';
import MetricCards from '../../GscTools/components/MetricCards';
import PeriodTrend from '../../GscTools/components/PeriodTrend';
import { metricsFor } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';

/**
 * The report itself: the headline metrics and the daily trend of whichever one is selected.
 *
 * They share a card because they are one reading of one scope — the cards say where the period
 * landed, the chart says how it got there, and picking a card re-points the chart without
 * fetching anything. Splitting them would put a heading between a number and its own history.
 *
 * The actions sit in this card's header rather than in an export block of their own, so each
 * download is next to the data it copies.
 */
function Overview({
    t,
    scope,
    current,
    baseline,
    comparison,
    metric,
    onSelectMetric,
    onRefresh,
    onDownloadCsv,
}) {
    const comparing = !!scope.baseline;
    const metrics = metricsFor(scope.searchType);

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">{t('tools.search_overview.overview.title')}</p>
                    <p className="gsc-tool-sub">
                        {comparing
                            ? t('tools.search_overview.overview.compared', {
                                  current: formatPeriod(scope.current),
                                  baseline: formatPeriod(scope.baseline),
                              })
                            : formatPeriod(scope.current)}
                    </p>
                </div>

                <div className="gsc-tool-icon-actions">
                    <button
                        type="button"
                        title={t('tools.gsc.controls.refresh')}
                        aria-label={t('tools.gsc.controls.refresh')}
                        onClick={onRefresh}
                        disabled={current?.status === 'loading'}
                    >
                        <i className="icon-refresh" />
                    </button>
                    <button
                        type="button"
                        title={t('tools.gsc.exports.report_csv')}
                        aria-label={t('tools.gsc.exports.report_csv')}
                        onClick={onDownloadCsv}
                        disabled={current?.status !== 'done'}
                    >
                        <i className="icon-fi_download" />
                    </button>
                </div>
            </div>

            <div className="card-body">
                {current?.status === 'loading' && <Loading padding={20} />}

                {current?.status === 'error' && (
                    <div className="gsc-tool-banner error">
                        {current.error ?? t('global.something_went_wrong')}
                    </div>
                )}

                {current?.status === 'done' && (
                    <>
                        <MetricCards
                            metrics={metrics}
                            totals={current?.totals}
                            priorTotals={baseline?.totals}
                            comparison={comparison}
                            selectedMetric={metric}
                            onSelectMetric={onSelectMetric}
                        />
                        <p className="gsc-tool-hint">
                            {t('tools.search_overview.overview.pick_card')}
                        </p>
                        <PeriodTrend
                            metric={metric}
                            current={current}
                            baseline={comparing ? baseline : null}
                            fileName={t('tools.gsc.trend.title')}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default withTranslation()(Overview);
